import { Spacer } from "@faire/web--source/slate/spacing";
import React from "react";

import { BrandInfo } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/BrandInfo/BrandInfo";
import { FullClickBrandInfo } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/BrandInfo/FullClickBrandInfo";
import { MaybeNewBrandLinkWrapper } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/BrandInfo/NewBrandTileRendererWrapper";
import { DebugInfoSection } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/DebugInfoSection/DebugInfoSection";
import { SearchProductImageClient } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductImage/search/SearchProductImageClient";
import { NotBoldProductName } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/PriceFirstAndNewBrandLinkXP/NotBoldProductName";
import { PriceFirstAndNewBrandLinkProductNameRenderer } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/PriceFirstAndNewBrandLinkXP/PriceFirstAndNewBrandLinkProductNameRenderer";
import { MaybePriceFirstProductTileWrapper } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/PriceText/MaybePriceFirstProductTileWrapper";
import { PriceWithClientFormatMoney } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/PriceText/PriceWithClientFormatMoney";
import { ProductName } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/ProductName";
import { SearchProductLink } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductLink/SearchProductLink";
import { ProductTileContainer } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileContainer/ProductTileContainer";
import { ProductTileOverlay } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileOverlay/ProductTileOverlay";
import { productTileOverlayProductTileAdaptor } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileOverlay/productTileOverlayUtils";
import { TextBadges } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/TextBadges/TextBadges";
import { SearchTileEffects } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/TileEffects/TileEffets";
import { TopBadges } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/TopBadges/TopBadges";
import { ProductTileContainerRefContextProvider } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileContainerRefContextProvider";
import { ProductTileContextProvider } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileContextProvider";
import { ProductTileSwatches } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileSwatches";
import { SearchProductTileProps } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/SearchProductTileProps";

import { brandInfoProductTileAdaptor } from "./__internal__/components/BrandInfo/brandInfoUtils";
import { Favorite } from "./__internal__/components/Favorite/Favorite";
import { ProductTileTrackingWrapper } from "./__internal__/components/ProductTileTrackingWrapper/ProductTileTrackingWrapper";
import { ProductTileUpperContainer } from "./__internal__/components/ProductTileUpperContainer/ProductTileUpperContainer";
import { QuickAdd } from "./__internal__/components/QuickAdd/QuickAdd";
import { textBadgesProductTileAdaptor } from "./__internal__/components/TextBadges/textBadgesUtils";

export const LoggedInSearchProductTile = ({
  data,
}: {
  data: SearchProductTileProps;
}) => {
  const {
    productTileImageProps,
    productTileTrackingWrapperProps,
    productTileDebugInfoProps,
    productTileTopBadgesProps,
    productTileFavoriteProps,
    productTileQuickAddProps,
    productTileClickProviderProps,
    productTile,
    brand,
    priceTextProps,
    insiderBadgeProps,
    productSearchString,
  } = data;

  const token = productTile.product?.token;
  if (!token) {
    // TODO loading state should probably live in the composable elements
    return null;
  }

  const brandInfoData = brandInfoProductTileAdaptor({
    productTile,
    brand,
    insiderBadgeProps,
    productSearchString,
    isLoggedInRetailer: true,
  });

  return (
    <ProductTileContextProvider brandToken={brand?.token} brand={undefined}>
      <SearchTileEffects />
      <ProductTileTrackingWrapper data={productTileTrackingWrapperProps}>
        <ProductTileContainer>
          <ProductTileContainerRefContextProvider>
            <ProductTileUpperContainer>
              <ProductTileOverlay
                data={productTileOverlayProductTileAdaptor(productTile, true)}
              />
              <SearchProductLink data={productTileClickProviderProps}>
                <SearchProductImageClient data={productTileImageProps} />
              </SearchProductLink>
              <QuickAdd showOnHover data={productTileQuickAddProps} />
              <Favorite data={productTileFavoriteProps} />
              <TopBadges data={productTileTopBadgesProps} />
              <DebugInfoSection data={productTileDebugInfoProps} />
            </ProductTileUpperContainer>
          </ProductTileContainerRefContextProvider>
          <Spacer size={{ mobileAndAbove: "1x", tabletAndAbove: 12 }} />
          <SearchProductLink data={productTileClickProviderProps}>
            <MaybePriceFirstProductTileWrapper isPriceFirstSlot={true}>
              <PriceWithClientFormatMoney {...priceTextProps} />
              <Spacer size="1/4x" />
            </MaybePriceFirstProductTileWrapper>
            <PriceFirstAndNewBrandLinkProductNameRenderer
              control={
                <ProductName productName={productTile?.product?.name ?? ""} />
              }
              treatment={
                <NotBoldProductName
                  productName={productTile?.product?.name ?? ""}
                />
              }
            />
            <MaybePriceFirstProductTileWrapper isPriceFirstSlot={false}>
              <PriceWithClientFormatMoney {...priceTextProps} />
            </MaybePriceFirstProductTileWrapper>
            <ProductTileSwatches optionDetails={productTile.options_details} />
          </SearchProductLink>
          <Spacer size={{ mobileAndAbove: 0, tabletAndAbove: "1x" }} />
          <MaybeNewBrandLinkWrapper
            oldBrandInfoComponent={<BrandInfo data={brandInfoData} />}
            newBrandInfoComponent={<FullClickBrandInfo data={brandInfoData} />}
          />
          <TextBadges data={textBadgesProductTileAdaptor(productTile)} />
        </ProductTileContainer>
      </ProductTileTrackingWrapper>
    </ProductTileContextProvider>
  );
};
